import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { Helmet } from '@/pages/home/components/SEOHead';
import { ArticleSchema } from '@/components/feature/SchemaOrg';
import { articles, categories } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16">
          <Breadcrumb />
          <section className="py-20">
            <div className="max-w-3xl mx-auto px-4 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-file-unknow-line text-2xl text-foreground-400" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
              <p className="text-foreground-500 text-sm mb-6">您访问的文章可能已被删除或不存在</p>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                返回分类浏览
              </Link>
            </div>
          </section>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === article.categoryId);
  const relatedArticles = articles
    .filter((a) => a.categoryId === article.categoryId && a.id !== article.id)
    .slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <Helmet
        title={`${article.title} - 52662鼎盛平台`}
        description={article.summary}
        canonicalUrl={`/article/${article.id}`}
        type="article"
      />
      <ArticleSchema article={article} />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb />

        <article className="py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <header className="mb-10">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                {category && (
                  <Link
                    to="/category"
                    className="px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium hover:bg-primary-100 transition-colors duration-200 whitespace-nowrap"
                  >
                    {category.name}
                  </Link>
                )}
                <span className="text-foreground-400 text-xs">{article.date}</span>
                <span className="text-foreground-400 text-xs flex items-center gap-1">
                  <i className="ri-eye-line text-xs" />
                  {article.readCount.toLocaleString()} 次阅读
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                {article.summary}
              </p>
            </header>

            <div
              className="prose-custom text-foreground-700 text-sm md:text-base leading-relaxed space-y-5"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/50">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors duration-200 whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line" />
                  返回分类浏览
                </Link>

                <div className="flex items-center gap-3">
                  <span className="text-foreground-400 text-xs">分享：</span>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 cursor-pointer" aria-label="分享到微信">
                    <i className="ri-wechat-line" />
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 cursor-pointer" aria-label="分享到微博">
                    <i className="ri-weibo-line" />
                  </button>
                  <button className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-primary-50 hover:text-primary-600 transition-colors duration-200 cursor-pointer" aria-label="复制链接">
                    <i className="ri-link" />
                  </button>
                </div>
              </div>
            </div>

            {relatedArticles.length > 0 && (
              <div className="mt-10 pt-8 border-t border-background-200/50">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5">相关文章</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group p-4 bg-background-100/60 rounded-lg border border-background-200/50 hover:border-primary-200/50 transition-all duration-200"
                    >
                      <h4 className="text-sm font-semibold text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {ra.title}
                      </h4>
                      <p className="text-xs text-foreground-500">{ra.date}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}